import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Snackbar,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import apiClient from '../../services/api';
import { getMaterialList, updateMaterials } from '../../services/applicationService';

// 构建完整的文件URL
const buildFileUrl = (filePath) => {
  if (!filePath) return '';
  
  // 如果已经是完整URL，直接返回
  if (filePath.startsWith('http://') || filePath.startsWith('https://') || filePath.startsWith('blob:')) {
    return filePath;
  }
  
  // 使用环境变量构建完整URL
  let attachmentUrl = process.env.REACT_APP_ATTACHMENT_URL || '';
  
  // 确保attachmentUrl不以斜杠结尾
  attachmentUrl = attachmentUrl.replace(/\/+$/, '');
  
  // 确保filePath不以斜杠开头
  const cleanFilePath = filePath.replace(/^\/+/, '');
  
  const fullUrl = `${attachmentUrl}/storage/${cleanFilePath}`;
  
  console.log('构建文件URL:', {
    originalPath: filePath,
    attachmentUrl: attachmentUrl,
    cleanFilePath: cleanFilePath,
    fullUrl: fullUrl
  });
  
  return fullUrl;
};

// 默认材料类型，将被API返回的数据替换
const DEFAULT_MATERIAL_TYPES = [
  { key: 'zippackage', label: '资料包', ifnecessary: 1 }
];

const MaterialsForm = ({ selectedProgram, selectedLevel, onSave, onCancel, loading = false, onSaveStateChange }) => {
  const [materials, setMaterials] = React.useState({});
  const [materialTypes, setMaterialTypes] = React.useState(DEFAULT_MATERIAL_TYPES);
  const [errors, setErrors] = React.useState({});
  const [saveError, setSaveError] = React.useState('');
  const [showErrorSnackbar, setShowErrorSnackbar] = React.useState(false);
  const [isDataModified, setIsDataModified] = React.useState(false);
  const [materialsLoading, setMaterialsLoading] = React.useState(false);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogContent, setDialogContent] = React.useState({ title: '', content: '', type: 'requirement' });

  // 监听数据变化，通知父组件保存按钮状态
  React.useEffect(() => {
    if (onSaveStateChange) {
      onSaveStateChange(canSave());
    }
  }, [materials, isDataModified, materialTypes, onSaveStateChange]);

  // 监听loading状态，触发保存
  React.useEffect(() => {
    if (loading) {
      setTimeout(() => {
        handleSave();
      }, 100);
    }
  }, [loading]);

  // 获取材料列表
  const fetchMaterialList = async () => {
    if (!selectedProgram || !selectedLevel) {
      return;
    }

    setMaterialsLoading(true);
    try {
      // 构建请求参数
      const s_id = selectedProgram.university?.s_id || selectedProgram.university?.id;
      const params = {
        s_id: s_id,
        degree: selectedLevel === '本科' ? 'Bachelor' : 
                selectedLevel === '硕士' ? 'Master' : 
                selectedLevel === '博士' ? 'Doctor' : 'Bachelor'
      };
      
      console.log('请求材料列表API，参数:', params);
      const newMaterialTypes = await getMaterialList(params);
      
      if (newMaterialTypes) {
        setMaterialTypes(newMaterialTypes);
        
        // 处理materials状态：如果材料有path且不为空，说明已上传
        const newMaterials = {};
        newMaterialTypes.forEach(material => {
          if (material.path && material.path.trim() !== '') {
            // 已上传的文件，构建完整的文件URL
            newMaterials[material.key] = {
              name: material.name || material.label,
              path: buildFileUrl(material.path),
              MaterialName: material.label
            };
          } else {
            // 未上传的文件，保留已选择的文件（如果有的话）
            newMaterials[material.key] = materials[material.key] || null;
          }
        });
        setMaterials(newMaterials);
      } else {
        // 如果API请求失败，使用默认材料类型
        setMaterialTypes(DEFAULT_MATERIAL_TYPES);
      }
    } catch (error) {
      console.error('获取材料列表失败:', error);
      // 如果API请求失败，使用默认材料类型
      setMaterialTypes(DEFAULT_MATERIAL_TYPES);
    } finally {
      setMaterialsLoading(false);
    }
  };

  // 每次组件加载或参数变化时都请求材料列表
  React.useEffect(() => {
    if (selectedProgram && selectedLevel) {
      fetchMaterialList();
    }
  }, [selectedProgram, selectedLevel]);

  // 处理弹窗显示
  const handleShowDialog = (content, type = 'requirement') => {
    // 如果是示例类型，需要构建完整的文件URL
    const processedContent = type === 'example' ? buildFileUrl(content) : content;
    
    setDialogContent({
      title: type === 'requirement' ? '材料要求' : '查看示例',
      content: processedContent,
      type: type
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  // 处理文件上传
  const handleFileUpload = (materialType, file) => {
    // 检查文件大小限制 (10MB = 10 * 1024 * 1024 bytes)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setSaveError(`文件大小不能超过10MB，当前文件大小: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
      setShowErrorSnackbar(true);
      return;
    }

    // 本地临时存储，不请求API
    const materialTypeInfo = materialTypes.find(t => t.key === materialType);
    setMaterials(prev => ({
      ...prev,
      [materialType]: {
        name: file.name,
        path: URL.createObjectURL(file), // 创建本地临时URL
        MaterialName: materialTypeInfo?.label || materialType,
        uploaded_at: new Date().toISOString(),
        file: file // 保存文件对象，用于后续上传
      }
    }));
    setIsDataModified(true);
  };

  // 处理文件删除
  const handleFileDelete = (materialType) => {
    // 本地临时删除，不请求API
    setMaterials(prev => {
      const newMaterials = { ...prev };
      const material = newMaterials[materialType];
      
      // 如果是本地临时URL，需要清理
      if (material && material.path && material.path.startsWith('blob:')) {
        URL.revokeObjectURL(material.path);
      }
      
      delete newMaterials[materialType];
      return newMaterials;
    });
    setIsDataModified(true);
  };

  // 验证表单
  const validateForm = () => {
    const newErrors = {};
    
    // 检查必填材料是否已上传
    materialTypes.forEach(type => {
      if (type.ifnecessary === 1 && !materials[type.key]) {
        newErrors[type.key] = `请上传${type.label}`;
      }
    });
    
    setErrors(newErrors);
    
    // 如果有错误，显示错误信息
    if (Object.keys(newErrors).length > 0) {
      setSaveError('请上传所有必选材料');
      setShowErrorSnackbar(true);
    }
    
    return Object.keys(newErrors).length === 0;
  };

  // 检查是否可以保存
  const canSave = () => {
    // 检查是否有数据变化
    if (!isDataModified) {
      return false;
    }

    // 检查所有必选材料是否已上传
    const requiredMaterials = materialTypes.filter(type => type.ifnecessary === 1);
    for (const materialType of requiredMaterials) {
      if (!materials[materialType.key]) {
        return false;
      }
    }

    return true;
  };

  // 处理保存
  const handleSave = async () => {
    console.log('开始保存材料');
    setSaveError('');
    
    if (!validateForm()) {
      console.log('表单验证失败');
      onSave && onSave(null);
      setShowErrorSnackbar(true);
      return;
    }

    try {
      const formData = new FormData();

      // 遍历所有材料，按照DocumentUpload.js的逻辑处理
      Object.entries(materials).forEach(([materialType, material]) => {
        if (material.file) {
          // 新上传的文件，提交二进制数据
          formData.append(materialType, material.file);
        } else if (material && typeof material === 'object' && material.path) {
          // 已上传未修改的文件，提交路径信息
          // 如果是完整URL，需要提取原始路径
          let originalPath = material.path;
          if (material.path.startsWith('http://') || material.path.startsWith('https://')) {
            // 从完整URL中提取原始路径
            const urlParts = material.path.split('/storage/');
            originalPath = urlParts.length > 1 ? urlParts[1] : material.path;
          }
          formData.append(`${materialType}[path]`, originalPath);
          formData.append(`${materialType}[name]`, material.name || 'unknown');
        }
      });

      // 文件上传API调用
      const result = await updateMaterials(formData);
      
      console.log('API响应结果:', result);

      if (result.success) {
        console.log('API调用成功，更新状态');
        // 使用API返回的数据直接更新状态
        if (result.data) {
          const updatedMaterials = {};
          Object.entries(result.data).forEach(([materialId, materialData]) => {
            updatedMaterials[materialId] = {
              name: materialData.name || '',
              path: buildFileUrl(materialData.path),
              MaterialName: materialData.MaterialName || ''
            };
          });
          setMaterials(updatedMaterials);
        }
        
        setIsDataModified(false);
        // 确保不显示错误提示
        setSaveError('');
        setShowErrorSnackbar(false);
        onSave && onSave({ materials: result.data });
      } else {
        console.log('API调用失败:', result.message);
        setSaveError(result.message || '保存失败');
        onSave && onSave(null);
        setShowErrorSnackbar(true);
      }
    } catch (error) {
      console.error('保存申请材料失败:', error);
      console.log('错误详情:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setSaveError(error?.response?.data?.message || error.message || '保存失败');
      onSave && onSave(null);
      setShowErrorSnackbar(true);
    }
  };

  // 渲染材料项
  const renderMaterialItem = (materialType) => {
    const material = materials[materialType.key];
    const isRequired = materialType.ifnecessary === 1;
    const hasError = errors[materialType.key];

    return (
      <Card key={materialType.key} sx={{ mb: 2, border: hasError ? '1px solid #f44336' : '1px solid #e0e0e0' }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {materialType.label}
              </Typography>
              {isRequired ? (
                <Chip label="必选" size="small" color="error" sx={{ height: 20 }} />
              ) : (
                <Chip label="可选" size="small" color="default" sx={{ height: 20 }} />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {materialType.requirement && (
                <IconButton
                  size="small"
                  onClick={() => handleShowDialog(materialType.requirement, 'requirement')}
                  title="查看材料要求"
                  sx={{ 
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.light',
                      color: 'white'
                    }
                  }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              )}
              {materialType.example && (
                <IconButton
                  size="small"
                  onClick={() => handleShowDialog(materialType.example, 'example')}
                  title="查看示例"
                  sx={{ 
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.light',
                      color: 'white'
                    }
                  }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              )}
              {material && (
                <>
                  <IconButton
                    size="small"
                    onClick={() => window.open(material.path, '_blank')}
                    title="下载"
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleFileDelete(materialType.key)}
                    title="删除"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Box>
          </Box>
          
          {material ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {material.name}
              </Typography>
              <Chip label="已上传" size="small" color="success" />
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <input
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                style={{ display: 'none' }}
                id={`file-upload-${materialType.key}`}
                type="file"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    handleFileUpload(materialType.key, file);
                  }
                }}
              />
              <label htmlFor={`file-upload-${materialType.key}`}>
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  sx={{ minWidth: 120 }}
                >
                  上传文件
                </Button>
              </label>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                支持 PDF、Word、图片格式，文件大小不超过10MB
              </Typography>
            </Box>
          )}
          
          {hasError && (
            <FormHelperText error sx={{ mt: 1 }}>
              {hasError}
            </FormHelperText>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowErrorSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowErrorSnackbar(false)} severity="error" sx={{ width: '100%' }}>
          {saveError}
        </Alert>
      </Snackbar>
      
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
        申请材料
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        请上传申请所需的材料文件。标有"必选"的材料必须上传，其他材料为可选。
      </Typography>

      {materialsLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>正在加载材料列表...</Typography>
        </Box>
      )}
      
      <Grid container spacing={2}>
        {materialTypes.map(materialType => (
          <Grid item xs={12} sm={6} key={materialType.key}>
            {renderMaterialItem(materialType)}
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          已上传 {Object.keys(materials).length} / {materialTypes.filter(t => t.ifnecessary === 1).length} 必选材料
        </Typography>
      </Box>

      {/* 材料要求弹窗 */}
      <Dialog 
        open={dialogOpen && dialogContent.type === 'requirement'} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          borderBottom: '1px solid #e0e0e0'
        }}>
          <InfoIcon color="primary" />
          {dialogContent.title}
        </DialogTitle>
        <DialogContent dividers sx={{ py: 2 }}>
          <Typography variant="body1" sx={{ 
            whiteSpace: 'pre-wrap',
            lineHeight: 1.6,
            fontSize: '0.95rem'
          }}>
            {dialogContent.content}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} color="primary" variant="contained">
            关闭
          </Button>
        </DialogActions>
      </Dialog>

      {/* 查看示例弹窗 */}
      <Dialog 
        open={dialogOpen && dialogContent.type === 'example'} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          borderBottom: '1px solid #e0e0e0'
        }}>
          <VisibilityIcon color="primary" />
          {dialogContent.title}
        </DialogTitle>
        <DialogContent dividers>
          {dialogContent.content && (
            <Box sx={{ textAlign: 'center' }}>
              {dialogContent.content.endsWith('.pdf') ? (
                <iframe 
                  src={dialogContent.content} 
                  width="100%" 
                  height="500px" 
                  style={{ border: 'none' }}
                  title="示例文档"
                />
              ) : (
                <img 
                  src={dialogContent.content} 
                  alt="示例图片" 
                  style={{ maxWidth: '100%', height: 'auto' }}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            关闭
          </Button>
          {dialogContent.content && (
            <Button 
              onClick={() => window.open(dialogContent.content, '_blank')} 
              color="primary"
              variant="outlined"
            >
              在新窗口打开
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MaterialsForm; 